import classNames from 'classnames';
import React, { MutableRefObject, useRef } from 'react';
import { IconButton } from 'src/common/icon-button';
import { Journey } from 'src/common/interfaces/journey.interface';
import { EditStrongIcon } from 'src/monet/icons';
import { useSegmentTrackedLink } from 'src/utils/segment-tracked-link.hook';
import { Nullable } from 'src/types/nullable.type';
import { JourneyWatermark } from './journey-watermark';
import { animated, useSpring, useSpringRef } from '@react-spring/web';
import { ScrollDirection, useScrollDirection } from '../utils/scroll-direction.hook';
import { useMount } from 'react-use';
import { NeueSharePanelButton } from '../share-panel/button';
import { useCurrentUser } from 'src/store/user';
import { TalkToJourneyButtonPlayer } from '../talk-to-journey/button-player';
import { LayoutMode } from '../types';

interface Props {
  isMobile: boolean;
  layoutMode: LayoutMode;
  previewMode: boolean;
  journey: Nullable<Journey>;
  onToggleMapRef: MutableRefObject<(isOpened: boolean) => void>;
  contentContainerElementRef: MutableRefObject<Nullable<HTMLDivElement>>;
}

const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const NeuePlayerTopBar = ({ journey, layoutMode, previewMode, isMobile, onToggleMapRef, contentContainerElementRef }: Props) => {
  const isHiddenRef = useRef(false);
  const isAnonymousUser = useCurrentUser((state) => !state.currentUser);
  const containerElementRef = useRef<Nullable<HTMLDivElement>>(null);

  const animateShowingTopBar = () => {
    if (!isHiddenRef.current) {
      return;
    }
    transitionApi.start({ y: 0, config: FLOAT_ANIMATION_CONFIG });
    isHiddenRef.current = false;
  };

  const animateHidingTopBar = () => {
    if (!containerElementRef.current || isHiddenRef.current) {
      return;
    }
    const { clientHeight } = containerElementRef.current;
    transitionApi.start({ y: -clientHeight, config: FLOAT_ANIMATION_CONFIG });
    isHiddenRef.current = true;
  };

  // const onScrollDirectionChanged = (direction: ScrollDirection) => {
  //   if (direction === 'down') {
  //     animateHidingTopBar();
  //     return;
  //   }
  //   animateShowingTopBar();
  // };

  // useScrollDirection(contentContainerElementRef.current, onScrollDirectionChanged, 5);

  // useMount(() => {
  //   if (isMobile) {
  //     onToggleMapRef.current = (opened: boolean) => {
  //       if (opened) {
  //         animateHidingTopBar();
  //       } else {
  //         animateShowingTopBar();
  //       }
  //     };
  //   }
  // });

  const editLinkRef = useSegmentTrackedLink('Player Edit Clicked', {
    context: 'player',
  });

  const transitionApi = useSpringRef();
  const [transitionStyles] = useSpring(
    () => ({
      ref: transitionApi,
      from: { y: 0 },
      to: { y: containerElementRef.current?.clientHeight },
    }),
    []
  );

  const isPreviewOnClientSide = typeof journey?.can_edit === 'undefined';

  const isEditingEnabled = isPreviewOnClientSide || journey?.can_edit;

  const hideWatermark = journey?.branding?.hide_journey_upsell || false;

  return (
    <animated.div
      ref={containerElementRef}
      className={classNames('fixed inset-x-0 top-0 z-neue-top-bar', {
        'py-6 px-6': !isMobile,
        'py-3 px-4 bg-neue-canvas-bg': isMobile,
      })}
      style={transitionStyles}
    >
      <div className='transition-colors text-neue-journey-bg flex place-content-between items-center'>
        <div>
          {!hideWatermark && (
            <JourneyWatermark
              isMobile={isMobile}
              slug={journey?.organization.slug}
              isAnonymousUser={isAnonymousUser}
              canUserEdit={isEditingEnabled}
            />
          )}
        </div>
        <div className='flex gap-2'>
          {!isMobile && (
            <a
              ref={editLinkRef}
              href={journey ? `/journeys/neue-edit/${journey.uuid}` : '#'}
              target='_self'
              rel='noopener noreferrer'
              className={classNames('transition-opacity', {
                'opacity-0 pointer-events-none': !isEditingEnabled,
                'opacity-100': isEditingEnabled,
              })}
            >
              <IconButton classes='!w-8 !h-8'>
                <EditStrongIcon />
              </IconButton>
            </a>
          )}
          {journey && !previewMode && <TalkToJourneyButtonPlayer journey={journey} layoutMode={layoutMode} />}
          {journey && <NeueSharePanelButton journey={journey} platform='player' />}
        </div>
      </div>
    </animated.div>
  );
};
